import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';

import { ButtonModule } from 'primeng/button';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { TooltipModule } from 'primeng/tooltip';

import { PermissionService } from '../services/permission.service';
import { LoadingState } from '../utilities/state.util';

import { BlueSageSettingsActions } from './bluesage-settings.actions';
import { blueSageSettingsFeature } from './bluesage-settings.feature';

/**
 * BlueSage provisioning — K3 and K4.
 *
 * Requirement: K3/K4 - a blank credential field means "leave unchanged", never
 * "erase" (secrets cannot be read back, and clearing a signing secret 401s every
 * inbound event silently); and `bluesage.locationId` belongs to the LOCATION
 * while the API key and webhook secret belong to the FRANCHISE, because a
 * franchise is one BlueSage org with many locations inheriting its credentials
 * (K17).
 *
 * Answers two questions: "can this zone take a payment right now?" and "if not,
 * what is missing?". Both come from the `blueSage` query, which reports where
 * each credential resolves FROM — `zone`, `env` or `missing` — and never its
 * value. A franchise inheriting the deployment's key and a franchise with its
 * own look identical otherwise, and that distinction is what causes support
 * calls.
 *
 * Two rules the form respects, because getting either wrong breaks money:
 *
 *  1. **A blank credential means "leave unchanged", never "erase".** Secrets
 *     cannot be read back, so the fields are always empty on load. Erasing a
 *     signing secret 401s every inbound event, silently.
 *  2. **The location id belongs to the location, the credentials to the
 *     franchise** (K17). A franchise is one BlueSage org with many locations
 *     under it, and config propagates upward, so a location zone carries only
 *     its own `bluesage.locationId` and inherits its franchise's keys.
 */
@Component({
    selector: 'app-bluesage-settings',
    // Standalone, following `tags-manager`: it declares what it needs and drops
    // onto any page with a single tag, so wiring it in touches one line elsewhere.
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        InputSwitchModule,
        InputTextModule,
        MessagesModule,
        TooltipModule,
    ],
    templateUrl: './bluesage-settings.component.html',
})
export class BlueSageSettingsComponent implements OnInit {

    actions = BlueSageSettingsActions;

    viewModel$ = this.store.select(createSelector({
        status: blueSageSettingsFeature.selectStatus,
        loading: createSelector(
            blueSageSettingsFeature.selectLoading,
            (loading) => loading === LoadingState.LOADING,
        ),
        saving: blueSageSettingsFeature.selectSaving,
        apiKey: blueSageSettingsFeature.selectApiKey,
        webhookSigningSecret: blueSageSettingsFeature.selectWebhookSigningSecret,
        locationId: blueSageSettingsFeature.selectLocationId,
        canSaveCredentials: createSelector(
            blueSageSettingsFeature.selectApiKey,
            blueSageSettingsFeature.selectWebhookSigningSecret,
            blueSageSettingsFeature.selectSaving,
            (apiKey, secret, saving) => !saving && Boolean(apiKey.trim() || secret.trim()),
        ),
        canSaveLocation: createSelector(
            blueSageSettingsFeature.selectLocationId,
            blueSageSettingsFeature.selectSaving,
            (locationId, saving) => !saving && Boolean(locationId.trim()),
        ),
    }));

    /**
     * The `*hasPermission` directive is the house idiom, but it lives in
     * `SharedModule`, and pulling that whole module into a standalone component
     * for one directive is a poor trade. These read from `PermissionService` —
     * exactly what the directive itself does, so same source, same answer.
     */
    canRead$ = this.permissions.watchAllPermissions([ 'bluesage.query' ]);
    canConfigure$ = this.permissions.watchAllPermissions([ 'bluesage.connect' ]);

    constructor(
        public store: Store,
        private permissions: PermissionService,
    ) { }

    ngOnInit(): void {
        this.store.dispatch(this.actions.panelOpened());
    }

    /** Where a credential came from, in words an operator can act on. */
    describeSource(source: string): string {
        switch (source) {
            case 'zone':
                return 'set for this franchise';
            case 'env':
                return 'inherited from the deployment';
            default:
                return 'not set';
        }
    }

    sourceIcon(source: string): string {
        return source === 'missing' ? 'pi pi-ban error-text' : 'pi pi-check success-text';
    }
}
